import * as React from 'react';
import * as _ from 'lodash';
import { MULTIMEDIA_TYPES, OPTION_SIZE } from '@nexxt/common/types';
import BubbleDragContainer from '../BubbleDragContainer';
import MediaManager from '../../../../../libraries/mediaLibrary/MediaManager';
import { UilMultiply } from '@iconscout/react-unicons';
import Popover from 'react-tiny-popover';
import Menu from '../../../../../common/Menu';
import { QuestionTypesMap } from '@nexxt/common/questionTypes';
import { optionSize } from '../../../../../../mapping/optionSettings';
import IncaVariableReferenceEditorModal from '../../../../../common/IncaVariable/IncaVariableReferenceEditorModal';
interface MediaBubbleProps {
    index: number; // for sortableElement
    content: any;
    langName: string;
    disableAddBubble?: boolean;
    disabled: boolean;
    setValue: (value, type: MULTIMEDIA_TYPES) => void;
    removeBubble: (languageCode: string, index: number) => void;
}

/**
 * This component provides UI for researcher to specify a MULTIMEDIA_TYPES media
 * which is embedded within a MultimediaBubble and is ultimately rendered on chatbot
 * according to its media type (e.g. zoomable image; playable video; or playable audio).
 */
const MediaBubble: React.FC<MediaBubbleProps> = ({
    index,
    content,
    langName,
    disableAddBubble,
    disabled,
    setValue,
    removeBubble
}: MediaBubbleProps) => {
    const [showMediaManager, setShowMediaManager] = React.useState(false);
    const [showDesignEditor, setShowDesignEditor] = React.useState(false);
    const [showAnswerReferenceModal, setShowAnswerReferenceModal] =
        React.useState(false);
    const [onMediaHover, setOnMediaHover] = React.useState(false);
    const isAnswerReference = !!content?.answerReference;
    let media;
    switch (content.type) {
        case MULTIMEDIA_TYPES.AUDIO:
            media = (
                <audio controls>
                    <source src={content.mediaURL} />
                </audio>
            );
            break;
        case MULTIMEDIA_TYPES.VIDEO:
            media = (
                <video controls>
                    <source src={content.mediaURL} />
                </video>
            );
            break;
        case MULTIMEDIA_TYPES.IMAGE:
            if (isAnswerReference) {
                const { answerReference } = content;
                const questionTypeItem =
                    QuestionTypesMap[answerReference?.selectedQuestion?.type];

                media = (
                    <div className="image--answer--reference">
                        <div className="image--answer--reference--wrapper">
                            <img src={questionTypeItem?.image} />
                            <p>ANSWER REFERENCE:</p>
                        </div>

                        <p className="image--answer--reference--title">
                            {answerReference?.selectedQuestion?.label}
                        </p>
                    </div>
                );
            } else {
                media = <img src={content.mediaURL} />;
            }
            break;
    }

    const applyCurrentMedia = (data: {
        mediaURL: string;
        label: string;
        type: string;
    }): void => {
        const { mediaURL, type } = data;
        setValue({ mediaURL }, type as MULTIMEDIA_TYPES);
        setShowMediaManager(false);
    };

    const updateMediaOptions = (data) => {
        setValue(data, content.type);
    };

    const updateAnswerReference = (formattingOptions) => {
        setValue(
            {
                answerReference: {
                    ...content.answerReference,
                    formattingOptions
                }
            },
            content.type
        );
        setShowAnswerReferenceModal(false);
    };
    const enableEditDesignModal = [
        MULTIMEDIA_TYPES.IMAGE,
        MULTIMEDIA_TYPES.VIDEO
    ].includes(content.type);

    const filteredOptionSize = optionSize.filter((size) => {
        if (size.value === OPTION_SIZE.AUTO) {
            return content.type === MULTIMEDIA_TYPES.IMAGE;
        }
        return true;
    });
    return (
        <>
            {showAnswerReferenceModal && (
                <IncaVariableReferenceEditorModal
                    showAnswerReferenceModal={showAnswerReferenceModal}
                    toggleShowAnswerReferenceModal={(toggle) =>
                        setShowAnswerReferenceModal(toggle)
                    }
                    langName={langName}
                    disabled={disabled}
                    answerReferenceQuestionValue={
                        content?.answerReference?.selectedQuestion?.value
                    }
                    formattingOptions={
                        content?.answerReference?.formattingOptions
                    }
                    isMediaBubble
                    handleImageReference={updateAnswerReference}
                />
            )}
            <BubbleDragContainer
                index={index}
                disableAddBubble={disableAddBubble}
                disableState={disabled}
            >
                <div
                    className="row my-2 mx-1 align-items-end"
                    onMouseEnter={(e) => setOnMediaHover(true)}
                    onMouseLeave={(e) => setOnMediaHover(false)}
                >
                    <div className="media--box">
                        {!disabled && (
                            <div
                                className="option-box--close"
                                onClick={(e) => removeBubble(langName, index)}
                            >
                                <i className="nx nx-close" />
                            </div>
                        )}

                        {media}
                    </div>
                    {!disabled &&
                        (onMediaHover ||
                            showMediaManager ||
                            showDesignEditor) && (
                            <>
                                {!isAnswerReference && (
                                    <MediaManager
                                        showMediaManager={showMediaManager}
                                        toggleMediaManager={setShowMediaManager}
                                        applyCurrentMedia={applyCurrentMedia}
                                        showMediaLibrary
                                    >
                                        <div
                                            className="hover-container"
                                            onClick={() =>
                                                setShowMediaManager(true)
                                            }
                                        >
                                            <div className="hover-container--item">
                                                Replace
                                            </div>
                                        </div>
                                    </MediaManager>
                                )}
                                {isAnswerReference && (
                                    <div
                                        className="hover-container"
                                        onClick={() =>
                                            setShowAnswerReferenceModal(true)
                                        }
                                    >
                                        <div className="hover-container--item">
                                            Open Reference Settings
                                        </div>
                                    </div>
                                )}
                                {enableEditDesignModal && (
                                    <Popover
                                        isOpen={showDesignEditor}
                                        onClickOutside={() =>
                                            setShowDesignEditor(false)
                                        }
                                        align="end"
                                        position={['right']}
                                        padding={10}
                                        content={
                                            <div className="options--container--popover--container">
                                                <div className="options--container--popover--header">
                                                    <span className="options--container--popover--header--title">
                                                        {content.type} Options
                                                    </span>
                                                    <span
                                                        className="options--container--popover--header--close"
                                                        onClick={() =>
                                                            setShowDesignEditor(
                                                                false
                                                            )
                                                        }
                                                    >
                                                        <UilMultiply
                                                            size={16}
                                                        />
                                                    </span>
                                                </div>
                                                <div className="options--container--popover--menu">
                                                    <div className="options--container--header--item ">
                                                        <div className="options--container--header--item--title">
                                                            Size
                                                        </div>
                                                        <div className="options--container--header--item--body">
                                                            <Menu
                                                                items={
                                                                    filteredOptionSize
                                                                }
                                                                selectedItem={
                                                                    content
                                                                        ?.mediaOptions
                                                                        ?.size
                                                                }
                                                                onClickItem={(
                                                                    item
                                                                ) =>
                                                                    updateMediaOptions(
                                                                        {
                                                                            mediaOptions:
                                                                                {
                                                                                    ...content?.mediaOptions,
                                                                                    size: item.value
                                                                                }
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <div
                                            className="hover-container"
                                            onClick={() =>
                                                setShowDesignEditor(true)
                                            }
                                        >
                                            <div className="hover-container--item">
                                                Edit Design
                                            </div>
                                        </div>
                                    </Popover>
                                )}
                            </>
                        )}
                </div>
            </BubbleDragContainer>
        </>
    );
};

export default MediaBubble;
